import {
  assignCourseBusinessAccounts,
  createCourse,
  patchCourse,
  removeCourseBusinessAccounts,
} from '@/services/courses.service'
import { normalizeBusinessAccountIds } from '@/utils/utils'
import {
  COURSE_SAVE_FLOW_ERROR_CODES,
  CUSTOM_CONTENT_ACCOUNT_PAGE_SIZE,
  CUSTOM_CONTENT_AUDIENCE_TYPES,
} from '@/constants/customContent'

const { CUSTOM, PUBLIC } = CUSTOM_CONTENT_AUDIENCE_TYPES
const {
  ASSIGN_FAILED,
  COURSE_SAVE_FAILED,
  PUBLIC_TO_CUSTOM_NOT_ALLOWED,
  UNASSIGN_FAILED,
} = COURSE_SAVE_FLOW_ERROR_CODES

const EMPTY_ACCOUNTS_RESPONSE = {
  count: 0,
  next: null,
  previous: null,
  results: [],
}

export class CourseSaveFlowError extends Error {
  constructor(code, message, cause = null, context = {}) {
    super(message)
    this.name = 'CourseSaveFlowError'
    this.code = code
    this.cause = cause
    this.context = context
  }
}

function isFeatureEnabled(flag) {
  return !!flag
}

export function buildAudienceTypePayload(isCustom) {
  return {
    audience_type: isCustom ? CUSTOM : PUBLIC,
  }
}

export function getAccountAssignmentDelta(
  initialAssignedAccountIds = [],
  currentAssignedAccountIds = []
) {
  const initial = normalizeBusinessAccountIds(initialAssignedAccountIds)
  const current = normalizeBusinessAccountIds(currentAssignedAccountIds)

  const initialSet = new Set(initial)
  const currentSet = new Set(current)

  const addedAccountIds = current.filter((id) => !initialSet.has(id))
  const removedAccountIds = initial.filter((id) => !currentSet.has(id))

  return {
    initialAssignedAccountIds: initial,
    currentAssignedAccountIds: current,
    addedAccountIds,
    removedAccountIds,
  }
}

export function buildEnterpriseAccountsLoaderState() {
  return {
    cache: {},
    pendingByKey: {},
    requestCount: 0,
    latestRequestId: 0,
  }
}

function buildEnterpriseAccountsKey(query = '', ids = []) {
  const normalizedIds = normalizeBusinessAccountIds(ids)
  if (normalizedIds.length) {
    return `ids:${normalizedIds.join(',')}`
  }
  return `name:${query.trim().toLowerCase()}`
}

function normalizeAccountsResponse(response = {}) {
  return {
    count: response.count || 0,
    next: response.next || null,
    previous: response.previous || null,
    results: Array.isArray(response.results) ? response.results : [],
  }
}

export async function loadEnterpriseAccounts({
  searchBusinessAccounts,
  loaderState,
  query = '',
  ids = [],
  pageSize = CUSTOM_CONTENT_ACCOUNT_PAGE_SIZE,
}) {
  if (!loaderState || typeof loaderState !== 'object') {
    throw new Error('A loaderState object is required')
  }

  const normalizedIds = normalizeBusinessAccountIds(ids)
  const normalizedQuery = query.trim()
  const key = buildEnterpriseAccountsKey(normalizedQuery, normalizedIds)

  if (loaderState.cache[key]) {
    return {
      response: loaderState.cache[key],
      stale: false,
      fromCache: true,
      requestId: loaderState.latestRequestId,
    }
  }

  if (loaderState.pendingByKey[key]) {
    return loaderState.pendingByKey[key]
  }

  loaderState.requestCount += 1
  const requestId = loaderState.requestCount
  loaderState.latestRequestId = requestId

  const params = normalizedIds.length
    ? {
        ids: normalizedIds,
        page: '1',
        page_size: Math.max(normalizedIds.length, Number(pageSize)).toString(),
      }
    : {
        page: '1',
        page_size: String(pageSize),
        ...(normalizedQuery ? { name: normalizedQuery } : {}),
      }

  const request = Promise.resolve(searchBusinessAccounts(params))
    .then((response) => {
      const normalizedResponse = normalizeAccountsResponse(response)
      loaderState.cache[key] = normalizedResponse

      return {
        response: normalizedResponse,
        stale: requestId !== loaderState.latestRequestId,
        fromCache: false,
        requestId,
      }
    })
    .finally(() => {
      delete loaderState.pendingByKey[key]
    })

  loaderState.pendingByKey[key] = request

  return request
}

export async function assignCourseAccounts({
  courseId,
  businessAccountIds = [],
  assignCourseAccountsRequest = assignCourseBusinessAccounts,
}) {
  const accountIds = normalizeBusinessAccountIds(businessAccountIds)
  if (!courseId || !accountIds.length) return null

  return assignCourseAccountsRequest({
    courseId,
    business_account_ids: accountIds,
  })
}

export async function removeCourseAccounts({
  courseId,
  businessAccountIds = [],
  removeCourseAccountsRequest = removeCourseBusinessAccounts,
}) {
  const accountIds = normalizeBusinessAccountIds(businessAccountIds)
  if (!courseId || !accountIds.length) return null

  return removeCourseAccountsRequest({
    courseId,
    business_account_ids: accountIds,
  })
}

function buildCoursePayload({
  course,
  company,
  files,
  customContentCoursesEnabled,
  isCreate,
}) {
  const payload = {
    ...course,
    company,
    files,
  }

  if (!isCreate) {
    payload.courseId = course.id
  }

  if (!customContentCoursesEnabled) {
    delete payload.audience_type
    delete payload.business_account_ids
    return payload
  }

  payload.audience_type =
    course.audience_type === CUSTOM
      ? CUSTOM
      : PUBLIC
  payload.business_account_ids =
    payload.audience_type === CUSTOM
      ? normalizeBusinessAccountIds(course.business_account_ids)
      : []

  return payload
}

function mergeCourseAudienceState({
  courseResponse,
  customContentCoursesEnabled,
  audienceType,
  businessAccountIds,
}) {
  if (!customContentCoursesEnabled) return courseResponse

  return {
    ...courseResponse,
    audience_type: audienceType,
    business_account_ids:
      audienceType === CUSTOM
        ? normalizeBusinessAccountIds(businessAccountIds)
        : [],
  }
}

export async function saveCourseWithCustomContentFlow({
  course,
  previousCourse = {},
  company,
  files = [],
  customContentCoursesEnabled = false,
  createCourseRequest = createCourse,
  patchCourseRequest = patchCourse,
  assignCourseAccountsRequest = assignCourseBusinessAccounts,
  removeCourseAccountsRequest = removeCourseBusinessAccounts,
}) {
  const featureEnabled = isFeatureEnabled(customContentCoursesEnabled)
  const isCreate = !course.id
  const audienceType =
    course.audience_type === CUSTOM
      ? CUSTOM
      : PUBLIC

  const currentAssignedAccountIds = normalizeBusinessAccountIds(
    course.business_account_ids
  )
  const initialAudienceType =
    previousCourse.audience_type === CUSTOM
      ? CUSTOM
      : PUBLIC
  const initialAssignedAccountIds = normalizeBusinessAccountIds(
    Array.isArray(previousCourse.business_account_ids)
      ? previousCourse.business_account_ids
      : previousCourse.assigned_accounts
  )

  if (
    featureEnabled &&
    !isCreate &&
    initialAudienceType === PUBLIC &&
    audienceType === CUSTOM
  ) {
    throw new CourseSaveFlowError(
      PUBLIC_TO_CUSTOM_NOT_ALLOWED,
      'Cannot convert a public course to custom'
    )
  }

  const payload = buildCoursePayload({
    course,
    company,
    files,
    customContentCoursesEnabled: featureEnabled,
    isCreate,
  })

  let savedCourse = null
  try {
    savedCourse = isCreate
      ? await createCourseRequest(payload)
      : await patchCourseRequest(payload)
  } catch (error) {
    throw new CourseSaveFlowError(
      COURSE_SAVE_FAILED,
      'Course save request failed',
      error
    )
  }

  if (!featureEnabled) {
    return savedCourse
  }

  const courseId = savedCourse.id || course.id

  if (audienceType === CUSTOM) {
    const {
      addedAccountIds,
      removedAccountIds,
    } = getAccountAssignmentDelta(
      initialAssignedAccountIds,
      currentAssignedAccountIds
    )

    if (addedAccountIds.length) {
      try {
        await assignCourseAccounts({
          courseId,
          businessAccountIds: addedAccountIds,
          assignCourseAccountsRequest,
        })
      } catch (error) {
        const courseWithAudienceState = mergeCourseAudienceState({
          courseResponse: savedCourse,
          customContentCoursesEnabled: featureEnabled,
          audienceType,
          businessAccountIds: initialAssignedAccountIds,
        })
        throw new CourseSaveFlowError(
          ASSIGN_FAILED,
          'Assign enterprise accounts failed',
          error,
          { courseId, course: courseWithAudienceState }
        )
      }
    }

    if (removedAccountIds.length) {
      try {
        await removeCourseAccounts({
          courseId,
          businessAccountIds: removedAccountIds,
          removeCourseAccountsRequest,
        })
      } catch (error) {
        const courseWithAudienceState = mergeCourseAudienceState({
          courseResponse: savedCourse,
          customContentCoursesEnabled: featureEnabled,
          audienceType,
          businessAccountIds: [
            ...initialAssignedAccountIds,
            ...addedAccountIds,
          ],
        })
        throw new CourseSaveFlowError(
          UNASSIGN_FAILED,
          'Remove enterprise accounts failed',
          error,
          { courseId, course: courseWithAudienceState }
        )
      }
    }
  }

  return mergeCourseAudienceState({
    courseResponse: savedCourse,
    customContentCoursesEnabled: featureEnabled,
    audienceType,
    businessAccountIds:
      audienceType === CUSTOM
        ? currentAssignedAccountIds
        : [],
  })
}

export function buildEmptyEnterpriseAccountsResponse() {
  return { ...EMPTY_ACCOUNTS_RESPONSE }
}
